import React, {memo, useEffect} from 'react'
import _ from 'lodash'
import {Button, Dropdown, Form, Grid, Header, List, Message, Segment} from 'semantic-ui-react'
import {asOptions, userIsAuthenticated} from '../../util'
import {withErrorHandling, withHelmet, withMeData, withPropsPresent, withQueryParams} from '../../hoc'
import {useSafeState, useScript} from '../../hook'
import Recaptcha from 'react-recaptcha'
import {sendHelpRequest} from '../../backend'

const _Help = (
  {
    me,
    query,
    sendHelpRequest
  }
) => {
  let recaptchaInstance

  const [recaptchaState, setRecaptchaState] = useSafeState({
    isRecaptchaShown: false,
    isRecaptchaVerified: false
  })

  const [state, setState] = useSafeState({
    from: '',
    topic: 'General',
    message: '',
    submitted: false,
    submitting: false,
    sent: false,
    errors: {}
  })

  useEffect(() => {
    setRecaptchaState({
      isRecaptchaShown: false,
      isRecaptchaVerified: false
    })
    setState({
      ...state,
      from: me && me.email ? me.email : '',
      topic: query.topic ? _.startCase(query.topic) : (userIsAuthenticated(me) ? 'Product Support' : 'General'),
    })
  }, [me])


  const validate = ({from, topic, message, errors = {}}) => {
    if (!from) {
      errors['lets.talk.from.required'] = 'Email required'
    } else {
      delete errors['lets.talk.from.required']
      if (from.length > 255) {
        errors['lets.talk.from.exceeds.max.length'] = 'Email cannot exceed 255 characters'
      } else {
        delete errors['lets.talk.from.exceeds.max.length']
      }
    }
    if (!topic) {
      errors['lets.talk.topic.required'] = 'Topic required'
    } else {
      delete errors['lets.talk.topic.required']
    }
    if (!message) {
      errors['lets.talk.message.required'] = 'Message required'
    } else {
      delete errors['lets.talk.message.required']
    }
    return errors
  }

  const onInputChange = (e) => {
    let updatedState = {...state, ...{[e.target.name]: e.target.value}}
    setState(updatedState)
  }

  const onLeave = (e) => {
    if (state.submitted) {
      setState({...state, errors: validate(state)})
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const errors = validate(state)
    delete errors['lets.talk.invalid.credentials']
    if (_.keys(errors).length) {
      setState({...state, ...{errors}})
      return
    }
    const updatedState = {...state, ...{submitting: true, submitted: true}}
    setState(updatedState)
    await sendHelpRequest(updatedState)
    setState({...updatedState, ...{submitting: false, sent: true}})
    setTimeout(() => {
      setState({
        ...state,
        sent: false,
        from: me && me.email ? me.email : '',
        topic: me && me.email ? 'Product Support' : 'Request Demo',
        message: ''
      })
    }, 10000)
  }

  const onRecaptchaShown = function () {
    setRecaptchaState({isRecaptchaVerified: false, isRecaptchaShown: true})
  }

  const onRecaptchaExpired = function () {
    recaptchaInstance.reset()
    setRecaptchaState({isRecaptchaVerified: false, isRecaptchaShown: true})
  }

  const onRecaptchaVerified = function () {
    setRecaptchaState({isRecaptchaShown: true, isRecaptchaVerified: true})
  }


  const {from, topic, message, errors, submitting} = state
  const hasEmailError = _.keys(errors).some((k) => k.indexOf('lets.talk.from') > -1)
  const hasTopicError = _.keys(errors).some((k) => k.indexOf('lets.talk.topic') > -1)
  const hasMessageError = _.keys(errors).some((k) => k.indexOf('lets.talk.message') > -1)
  const hasErrors = hasEmailError || hasTopicError || hasMessageError
  const isDemoRequest = topic.toLowerCase() === 'request demo'
  let topics = ['Request Demo', 'Product Support', 'Billing', 'General']
  if (!userIsAuthenticated(me)) {
    topics = ['Request Demo', 'General']
  }

  return (
    <Grid>
      <Grid.Row columns='16' centered>
        <Grid.Column width='8'>
          <Header as='h1'>How Can We Help?</Header>
          <Segment>
            {
              state.sent && (
                <Message success>
                  <Message.Header>
                    Your message was received!
                  </Message.Header>
                  <Message.List>
                    <Message.Item>We do our best to reply within 60 minutes Mon-Fri.</Message.Item>
                    <Message.Item>We monitor on Sat/Sun but our response times will vary.</Message.Item>
                  </Message.List>
                </Message>
              )
            }
            {
              !state.sent && (
                <Form error={hasErrors} onSubmit={onSubmit} loading={!userIsAuthenticated(me) && !recaptchaState.isRecaptchaShown}>
                  <Form.Field error={hasTopicError} required>
                    <label>Topic</label>
                    <Dropdown options={asOptions(topics)} selection item
                              value={topic}
                              placeholder='Select topic'
                              onBlur={onLeave}
                              onChange={(e, {value}) => onInputChange({
                                target: {
                                  name: 'topic',
                                  value
                                }
                              })}/>
                  </Form.Field>
                  {
                    (!userIsAuthenticated(me)) && (
                      <Form.Field error={hasEmailError} required>
                        <label>Your Email</label>
                        <Form.Input placeholder='Enter your email'
                                    name='from'
                                    value={from}
                                    autoComplete='false'
                                    onBlur={onLeave}
                                    onChange={onInputChange}/>
                      </Form.Field>
                    )
                  }
                  <Form.Field error={hasMessageError} required>
                    <label>{isDemoRequest ? 'Tell Us A Little More' : 'Please, tell us more'}</label>
                    <Form.TextArea
                      placeholder={isDemoRequest ? `Tell us a little about you. \n\nWhat company do you work for? \n\nWhat's your role? \n\nWhat were you hoping Service Mocks could help you with?` : 'How can we help?'}
                      name='message'
                      value={message}
                      rows='10'
                      onBlur={onLeave}
                      onChange={onInputChange}/>
                  </Form.Field>
                  <Message error>
                    <Message.Content>
                      <List bulleted>
                        {
                          _.keys(errors).map((key) => (
                            <List.Item key={key}>{errors[key]}</List.Item>
                          ))
                        }
                      </List>
                    </Message.Content>
                  </Message>
                  {
                    !userIsAuthenticated(me) && (
                      <Recaptcha
                        sitekey="6LdSJTMUAAAAACzcKunXqcWH-1bpK0y27Ww9W_fO"
                        render="explicit"
                        verifyCallback={() => onRecaptchaVerified()}
                        onloadCallback={() => onRecaptchaShown()}
                        expiredCallback={() => onRecaptchaExpired()}
                      />
                    )
                  }
                  <div style={{marginTop: '15px'}}>
                    <Button disabled={!userIsAuthenticated(me) && !recaptchaState.isRecaptchaVerified} primary onClick={onSubmit}
                            loading={submitting}>{userIsAuthenticated(me) || recaptchaState.isRecaptchaVerified ? 'Send' : `Send If You're Human`}</Button>
                  </div>
                </Form>
              )
            }
          </Segment>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

let Help = (props) => {
  useScript({src: 'https://www.google.com/recaptcha/api.js?render=explicit', crossorigin: true})
  const {handleError} = props
  props = {...props, sendHelpRequest: handleError(sendHelpRequest)}
  return (<_Help {...props}/>)
}

Help = withHelmet(Help, 'Help')
Help = withPropsPresent(Help, ['meLoaded'])
Help = withMeData(Help)
Help = withErrorHandling(Help)
Help = withQueryParams(Help)
Help = memo(Help)

export {Help, _Help}

export default Help